import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import "./SafetyChatbot.css";

function SafetyChatbot() {
  const [isOpen, setIsOpen] = useState(false); // control expand/collapse
  const [messages, setMessages] = useState([]); // store chat history
  const [userInput, setUserInput] = useState(""); // current input text
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const toggleChat = () => {
    if (!isOpen) {
      // Only add welcome message when opening
      setMessages([
        {
          role: "assistant",
          content:
            "👋  I’m here to help you make safer choices — ask me about crime stats, safe suburbs, or anything safety-related.",
        },
      ]);
    }
    setIsOpen(!isOpen);
  };

  const handleSend = async () => {
    if (!userInput.trim()) return;

    const newMessages = [...messages, { role: "user", content: userInput }];
    setMessages(newMessages);
    setUserInput("");
    setIsTyping(true);

    try {
      const response = await fetch(
        "https://langchain-agent-y70x.onrender.com/agent",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: userInput,
          }),
        }
      );
      /*const response = await fetch(
        "https://hu1ig95cke.execute-api.ap-southeast-2.amazonaws.com/chat",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: userInput,
            session_id:
              "frontend-session-" + Math.random().toString(36).substring(7),
          }),
        }
      );*/

      const data = await response.json();
      let botReply = data.reply;

      // --- Smart Recommendation Section ---
      const lowered = userInput.toLowerCase();
      if (
        lowered.includes("walk") ||
        lowered.includes("walking") ||
        lowered.includes("night")
      ) {
        botReply +=
          "\n\n🛣️ You might find our **[Night Walk Planner](https://hersafehome.me/route)** helpful to plan safer routes.";
      } else if (
        lowered.includes("move") ||
        lowered.includes("suburb") ||
        lowered.includes("neighborhood") ||
        lowered.includes("where i live")
      ) {
        botReply +=
          "\n\n🏡 You might find our **[SafeRent Map](https://hersafehome.me/map)** helpful for choosing safer neighborhoods.";
      } else if (
        lowered.includes("house") ||
        lowered.includes("home safety") ||
        lowered.includes("break-in") ||
        lowered.includes("secure home")
      ) {
        botReply +=
          "\n\n🏠 You might like our **[SafeHouse 3D Guide](https://hersafehome.me/model)** for tips on securing your home.";
      }
      // --- End of Smart Section ---

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: botReply },
      ]);
    } catch (error) {
      console.error("Error:", error);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "⚠️ Error contacting the server." },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={toggleChat}
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 9999,
          width: "60px",
          height: "60px",
          borderRadius: "50%",
          backgroundColor: "#007bff",
          color: "white",
          fontSize: "24px",
          border: "none",
          cursor: "pointer",
        }}
        className="chatbot-button"
      >
        💬
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            bottom: "90px",
            right: "20px",
            width: "300px",
            height: "400px",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "10px",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "10px",
              borderBottom: "1px solid #ddd",
              fontWeight: "bold",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            🛡️ Safety Assistant
            <button
              onClick={toggleChat}
              style={{
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
                color: "#999",
              }}
            >
              ❌
            </button>
          </div>

          {/* Chat Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "10px" }}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: "10px",
                  textAlign: msg.role === "user" ? "right" : "left",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    padding: "8px 12px",
                    borderRadius: "15px",
                    backgroundColor:
                      msg.role === "user" ? "#007bff" : "#f1f1f1",
                    color: msg.role === "user" ? "white" : "black",
                    maxWidth: "80%",
                    wordBreak: "break-word",
                  }}
                >
                  {msg.role === "assistant" ? (
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div style={{ marginBottom: "10px", textAlign: "left" }}>
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div
            style={{
              padding: "10px",
              borderTop: "1px solid #ddd",
              display: "flex",
            }}
          >
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder="Type a message..."
              style={{
                flex: 1,
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                marginRight: "5px",
              }}
            />
            <button onClick={handleSend} style={{ padding: "8px 12px" }}>
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default SafetyChatbot;
